import { Fraunces_400Regular, Fraunces_600SemiBold, Fraunces_700Bold } from '@expo-google-fonts/fraunces';
import {
  LibreFranklin_400Regular,
  LibreFranklin_500Medium,
  LibreFranklin_600SemiBold,
  LibreFranklin_700Bold,
} from '@expo-google-fonts/libre-franklin';
import { SpaceMono_400Regular, SpaceMono_700Bold } from '@expo-google-fonts/space-mono';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react-native';
import { useFonts } from 'expo-font';
import { DarkTheme, DefaultTheme, ThemeProvider } from 'expo-router/react-navigation';
import { Stack } from 'expo-router';
import { useEffect } from 'react';
import { ActivityIndicator, Platform, useColorScheme } from 'react-native';
import { applyTextSize, loadTextSize } from '@/lib/text-size-storage';
import { hitSlopCss } from '@/lib/hit-slop';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { Button, Text, TamaguiProvider, Theme, View, YStack } from 'tamagui';

import { ErrorBoundary } from '@/components/error-boundary';
import { OfflineBanner } from '@/components/offline-banner';
import { AuthProvider, useAuth } from '@/hooks/auth-context';
import { setUpAppStateFocusListener, setUpNetworkStatusListener } from '@/lib/network';
import { ResetPassword } from '@/screens/reset-password';
import { darkColors, lightColors } from '../theme/tokens.generated';
import tamaguiConfig from '../../tamagui.config';

const queryClient = new QueryClient();

// Issue #152: must run before anything mounts, once -- see network.ts's
// own doc comment for why this is what makes every mutation's default
// networkMode: 'online' actually see React Native's real connectivity.
setUpNetworkStatusListener();

// Issue #395: gives TanStack's focusManager React Native's AppState, so
// backgrounded apps stop polling (the /me/activity badge poller) and
// foregrounding refetches. Web keeps the library's own visibilitychange.
// Once, before anything mounts -- same reasoning as the line above.
setUpAppStateFocusListener();

// Sentry crash reporting (issue #142). A missing DSN (local dev without
// .env.local set up) leaves the SDK disabled rather than throwing --
// documented @sentry/react-native behavior for an empty dsn, same
// "never blocks the app" posture as the backend's initSentry. No PII:
// sendDefaultPii defaults to false in this SDK (unlike sentry-go, whose
// default is true), so nothing to override here.
Sentry.init({
  dsn: process.env.EXPO_PUBLIC_SENTRY_DSN,
  environment: process.env.EXPO_PUBLIC_SENTRY_ENVIRONMENT ?? 'development',
  release: process.env.EXPO_PUBLIC_SENTRY_RELEASE,
  tracesSampleRate: 0.1,
});

// React Navigation's own chrome (Stack headers, Tabs bar) doesn't read
// Tamagui's theme -- it needs its own ThemeProvider pointed at the same
// brand colors, or it renders default black-on-white regardless of
// colorScheme (this is what left the sign-up modal's header pure white
// in dark mode, caught by adversarial review, not by the screenshots
// this session had taken -- none of them exercised any nav chrome).
function navigationTheme(mode: 'light' | 'dark') {
  const colors = mode === 'dark' ? darkColors : lightColors;
  const base = mode === 'dark' ? DarkTheme : DefaultTheme;
  return {
    ...base,
    colors: {
      ...base.colors,
      background: colors.surfacePage,
      card: colors.surfaceCard,
      text: colors.textPrimary,
      border: colors.borderDefault,
      primary: colors.actionPrimary,
      // The tab badge reads `notification`; its default is a third, system red.
      notification: colors.actionPrimary,
    },
  };
}

// The nav gate itself -- see useAuthState's doc comment for the full
// state list and why this isn't a plain signed-in/signed-out check.
// Stack.Protected keeps the guard declarative: each group renders only
// when its condition is true, with Expo Router handling the redirect
// on state transitions. AuthProvider gives every screen the SAME auth
// state via context, rather than each screen deriving its own copy
// (see auth-context.tsx for why that mattered).
//
// Font names here must match tamagui.config.ts's `face` map exactly --
// that's what ties a given fontWeight to the right loaded file. Only
// the weights actually used by the design tokens are loaded (see
// tokens/typography.json and the landing page's own font <link>,
// frontend's real cross-platform equivalent of it).
export default function RootLayout() {
  return (
    // SafeAreaProvider was NEVER mounted anywhere in this app until
    // now -- confirmed by grep, zero matches for "SafeAreaProvider"
    // in src/ before this change. Every screen's own
    // useSafeAreaInsets() call (ScreenContainer, the tab bar) has
    // been running with no real Provider ancestor this whole time;
    // that hook throws outright with no Provider
    // (react-native-safe-area-context's own source, NO_INSETS_ERROR),
    // so something -- almost certainly a fallback this app never
    // deliberately set up -- has been silently supplying SOME insets
    // value instead of a hard crash. This is the real, structural
    // root cause behind this session's own repeated, OSCILLATING tab-
    // bar-padding bugs: "cut off" -> bumped the floor -> "too thick
    // on desktop" -> made it viewport-aware -> "extra-underpadded
    // again" once the explicit height was reverted. Every one of
    // those fixes tuned the MATH around insets.bottom without ever
    // fixing WHERE that number actually came from -- if it was never
    // a real, live, CSS-env()-driven value (which is exactly what
    // public/index.html's viewport-fit=cover meta exists to feed),
    // no amount of Math.max()/breakpoint tuning around it could ever
    // converge. Mounted here, as the outermost provider (before
    // GestureHandlerRootView, matching this library's own documented
    // placement -- as high in the tree as possible, above anything
    // that reads insets), so useSafeAreaInsets() everywhere in this
    // app finally reads a real, live value instead of an unintentional
    // fallback.
    <SafeAreaProvider>
      {/* GestureHandlerRootView wraps the whole app -- required for
        react-native-gesture-handler to work reliably at all (its own
        setup docs: without this, gestures can silently fail to
        recognize, especially on Android). Added alongside
        react-native-draggable-flatlist (profile/index.tsx's photo
        grid) -- the first real gesture-driven interaction this app has
        shipped; nothing used gesture-handler directly before this, only
        transitively through expo-router/react-navigation's own
        internals. */}
      <GestureHandlerRootView style={{ flex: 1 }}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <RootLayoutContent />
          </AuthProvider>
        </QueryClientProvider>
      </GestureHandlerRootView>
    </SafeAreaProvider>
  );
}

// Web-only, injected at runtime rather than via `app/+html.tsx` -- this
// app's own app.json sets web.output: "single" (a plain SPA, one
// index.html), and Expo Router's +html.tsx customization only applies
// to "static"/"server" output modes, confirmed the hard way: a real
// +html.tsx file with this exact CSS was added, exported via
// `expo export --platform web`, and the resulting dist/index.html
// still had zero trace of it -- byte-identical to the stock Expo
// template. Deleted that dead file rather than leave a customization
// that silently does nothing. A runtime <style> tag has no such
// dependency on the output mode, and also fixes the dev server (expo
// start --web uses an entirely different HTML template that +html.tsx
// never touched anyway) -- founder, 2026-09-16: "there's that fucking
// scroll bar again, it's unacceptable."
const SCROLLBAR_STYLE_ID = 'nearkin-scrollbar-style';
const SCROLLBAR_CSS = `
  * {
    scrollbar-width: thin;
    scrollbar-color: rgba(121, 108, 93, 0.35) transparent;
  }
  *::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  *::-webkit-scrollbar-track {
    background: transparent;
  }
  *::-webkit-scrollbar-thumb {
    background-color: rgba(121, 108, 93, 0.35);
    border-radius: 999px;
  }
  *::-webkit-scrollbar-thumb:hover {
    background-color: rgba(121, 108, 93, 0.55);
  }
  ${hitSlopCss()}
`;

function useWebScrollbarStyle() {
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (document.getElementById(SCROLLBAR_STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = SCROLLBAR_STYLE_ID;
    style.textContent = SCROLLBAR_CSS;
    document.head.appendChild(style);
  }, []);
}

function RootLayoutContent() {
  const colorScheme = useColorScheme();
  const auth = useAuth();
  useWebScrollbarStyle();
  useEffect(() => {
    // Re-apply the saved text size (issue #319) before first paint settles.
    void loadTextSize().then(applyTextSize);
  }, []);
  const [fontsLoaded, fontError] = useFonts({
    Fraunces_400Regular,
    Fraunces_600SemiBold,
    Fraunces_700Bold,
    LibreFranklin_400Regular,
    LibreFranklin_500Medium,
    LibreFranklin_600SemiBold,
    LibreFranklin_700Bold,
    SpaceMono_400Regular,
    SpaceMono_700Bold,
  });
  const theme = colorScheme === 'dark' ? 'dark' : 'light';

  return (
    <TamaguiProvider config={tamaguiConfig} defaultTheme={theme}>
      <Theme name={theme}>
        <ErrorBoundary>
          <ThemeProvider value={navigationTheme(theme)}>
            {/* Issue #152 AC1: mounted once, above every screen
                (including sign-in), rather than duplicated per list
                screen -- the same connectivity fact is true everywhere
                at once. */}
            <OfflineBanner />
            {auth.status === 'loading' || (!fontsLoaded && !fontError) ? (
              <View flex={1} alignItems="center" justifyContent="center" backgroundColor="$background">
                <ActivityIndicator />
              </View>
            ) : auth.status === 'password-recovery' ? (
              // A password-reset link click establishes a real session,
              // same as a normal sign-in -- without this check the auth
              // gate below would route on that session as usual and skip
              // "set a new password" entirely. See use-auth-state.ts's
              // deriveAuthState, which short-circuits on this status
              // before any of the normal signed-in/out branching.
              <ResetPassword />
            ) : auth.status === 'error' ? (
              <YStack
                flex={1}
                alignItems="center"
                justifyContent="center"
                gap="$3"
                backgroundColor="$background"
                px="$6"
              >
                <Text color="$color" textAlign="center">
                  Couldn&apos;t load your account. Check your connection and try again.
                </Text>
                <Button onPress={auth.retry}>Retry</Button>
              </YStack>
            ) : (
              // Issue #147 AC4: no `animation`/`animationDuration` override
              // here -- checked, and left at expo-router's default on
              // purpose. This is a NATIVE-STACK navigator; its push/pop
              // transition is the platform's own OS-level animation
              // (UINavigationController on iOS, Fragment transitions on
              // Android), not a JS-driven duration this app could tokenize
              // without fighting platform convention (this project's own
              // "rely on the ecosystem, don't hand-roll" principle). It
              // already gets reduce-motion for free at that OS layer --
              // confirmed general native-stack behavior, not re-solved
              // here. The real, actionable reduce-motion gap was
              // elsewhere: web's CSS transitions and Skeleton's own
              // setInterval pulse, neither of which had any built-in
              // accessibility awareness -- see use-reduced-motion.ts and
              // its wiring into skeleton.tsx.
              <Stack screenOptions={{ headerShown: false }}>
                {/* needs-signup renders the same (unauth) group as
                  signed-out -- the sign-up screen itself checks
                  useAuthState() and renders its age-attestation step
                  instead of the auth-method picker when already
                  authenticated. See Preview's own needs-signup redirect
                  for why a needs-signup user can't get stuck on it. */}
                <Stack.Protected guard={auth.status === 'signed-out' || auth.status === 'needs-signup'}>
                  <Stack.Screen name="(unauth)" />
                </Stack.Protected>
                {/* Tier 0 is opt-in (issue #70, founder decision
                  2026-09-05): verification-pending is no longer a state
                  the gate routes to -- it's a screen a signed-up user
                  pushes from their Profile ("Confirm you're a real person").
                  Declared here without Protected so the route exists
                  and renders when pushed, and never as a wall. */}
                <Stack.Screen name="verification-pending" />
                {/* Issue #134: terms/privacy need to be reachable from
                  BOTH sign-up (signed-out) and, once #87 ships, Settings
                  (signed-in) -- same reasoning as verification-pending
                  above, registered without Stack.Protected so neither
                  auth state blocks the route. */}
                <Stack.Screen name="terms" />
                <Stack.Screen name="privacy" />
                <Stack.Protected guard={auth.status === 'signed-up'}>
                  <Stack.Screen name="(app)" />
                </Stack.Protected>
              </Stack>
            )}
          </ThemeProvider>
        </ErrorBoundary>
      </Theme>
    </TamaguiProvider>
  );
}
